import { useState, useEffect } from 'react';
import './EventManager.css';

const EventManager = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    time: '',
    location: '',
    description: '',
    category: 'meeting',
    notifyMembers: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await fetch('http://localhost:3001/api/events');
      const data = await response.json();
      setEvents(data.events || []);
    } catch (err) {
      console.error('Error fetching events:', err);
      setMessage({ type: 'error', text: 'Failed to load events' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('adminToken');
      const url = editingEvent 
        ? `http://localhost:3001/api/events/${editingEvent._id}`
        : 'http://localhost:3001/api/events';
      
      const method = editingEvent ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ 
          type: 'success', 
          text: editingEvent 
            ? 'Event updated successfully!' 
            : `Event created successfully! ${formData.notifyMembers ? `(${data.emailStats?.sent || 0} emails sent)` : ''}`
        });
        resetForm();
        fetchEvents();
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to save event' });
      }
    } catch (err) {
      console.error('Error saving event:', err);
      setMessage({ type: 'error', text: 'Failed to save event' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      date: new Date(event.date).toISOString().split('T')[0],
      time: event.time || '',
      location: event.location || '',
      description: event.description || '',
      category: event.category || 'meeting',
      notifyMembers: false
    });
    setShowForm(true);
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`http://localhost:3001/api/events/${eventId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Event deleted successfully!' });
        fetchEvents();
      } else {
        const data = await response.json();
        setMessage({ type: 'error', text: data.message || 'Failed to delete event' });
      }
    } catch (err) {
      console.error('Error deleting event:', err);
      setMessage({ type: 'error', text: 'Failed to delete event' });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      date: '',
      time: '',
      location: '',
      description: '',
      category: 'meeting',
      notifyMembers: false
    });
    setEditingEvent(null);
    setShowForm(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getCategoryBadge = (category) => {
    const badges = {
      meeting: { label: 'Meeting', color: '#3b82f6' },
      festival: { label: 'Festival', color: '#f59e0b' },
      cultural: { label: 'Cultural', color: '#8b5cf6' },
      spiritual: { label: 'Spiritual', color: '#10b981' },
      community: { label: 'Community', color: '#ec4899' },
      other: { label: 'Other', color: '#6b7280' }
    };
    
    const badge = badges[category] || badges.other;
    return (
      <span className="event-badge" style={{ backgroundColor: badge.color }}>
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return <div className="event-manager-loading">Loading events...</div>;
  }

  return (
    <div className="event-manager">
      <div className="event-manager-header">
        <h2>📅 Event Manager</h2>
        <button 
          className="btn-create-event" 
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? '← Back to List' : '+ Create Event'}
        </button>
      </div>

      {message.text && (
        <div className={`event-message event-message-${message.type}`}>
          {message.text}
        </div>
      )}

      {showForm ? (
        <div className="event-form-container">
          <h3>{editingEvent ? 'Edit Event' : 'Create New Event'}</h3>
          <form onSubmit={handleSubmit} className="event-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="title">Event Title *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Community Meeting"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="date">Date *</label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="time">Time</label>
                <input
                  type="time"
                  id="time"
                  name="time"
                  value={formData.time}
                  onChange={handleInputChange}
                  placeholder="e.g., 10:00 AM"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="location">Location</label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="e.g., SNDP Hall"
                />
              </div>

              <div className="form-group">
                <label htmlFor="category">Category *</label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                >
                  <option value="meeting">Meeting</option>
                  <option value="festival">Festival</option>
                  <option value="cultural">Cultural</option>
                  <option value="spiritual">Spiritual</option>
                  <option value="community">Community</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="4"
                placeholder="Event details..."
              />
            </div>

            <div className="form-group-checkbox">
              <label>
                <input
                  type="checkbox"
                  name="notifyMembers"
                  checked={formData.notifyMembers}
                  onChange={handleInputChange}
                />
                <span>📧 Notify all members via email with calendar invite</span>
              </label>
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                className="btn-cancel" 
                onClick={resetForm}
                disabled={submitting}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn-submit"
                disabled={submitting}
              >
                {submitting ? 'Saving...' : editingEvent ? 'Update Event' : 'Create Event'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="events-list">
          {events.length === 0 ? (
            <div className="no-events">
              <p>No events yet. Create your first event!</p>
            </div>
          ) : (
            <div className="events-grid">
              {events.map(event => (
                <div key={event._id} className="event-card">
                  <div className="event-card-header">
                    <h3>{event.title}</h3>
                    {getCategoryBadge(event.category)}
                  </div>
                  
                  <div className="event-card-body">
                    <div className="event-detail">
                      <span className="event-icon">📅</span>
                      <span>{formatDate(event.date)}</span>
                    </div>
                    
                    {event.time && (
                      <div className="event-detail">
                        <span className="event-icon">🕐</span>
                        <span>{event.time}</span>
                      </div>
                    )}
                    
                    {event.location && (
                      <div className="event-detail">
                        <span className="event-icon">📍</span>
                        <span>{event.location}</span>
                      </div>
                    )}
                    
                    {event.description && (
                      <p className="event-description">{event.description}</p>
                    )}
                  </div>

                  <div className="event-card-actions">
                    <button 
                      className="btn-edit"
                      onClick={() => handleEdit(event)}
                    >
                      ✏️ Edit
                    </button>
                    <button 
                      className="btn-delete"
                      onClick={() => handleDelete(event._id)}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EventManager;
